import { useEffect, useState, type ReactNode } from 'react';
import { useMutation } from '@apollo/client';
import { isValidEpisodeCount, EPISODE_COUNT_MIN, EPISODE_COUNT_MAX } from '@courseai/domain';
import { useT } from '../i18n';
import {
  APPROVE_COURSE_STRUCTURE,
  parseProposedEpisodes,
  parseProposedOverview,
  type ApproveCourseStructureData,
  type ApproveCourseStructureVariables,
  type AgentGenerationRequest,
} from '../graphql/agent';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// An editable row in the structure-review table (Req 5.2, 5.3). Durations are
// edited as strings so the input can be transiently empty while typing; they
// are parsed to integers on submit.
export interface EditableEpisode {
  readonly title: string;
  readonly durationText: string;
  /** The agent's summary of this episode (read-only, shown for context). */
  readonly summary?: string;
}

// Build the initial editable-episode list from a request's proposedStructure.
export function episodesFromRequest(
  request: AgentGenerationRequest,
): ReadonlyArray<EditableEpisode> {
  return parseProposedEpisodes(request.proposedStructure).map((episode) => ({
    title: episode.title ?? '',
    durationText: String(episode.proposedDurationSeconds ?? 0),
    ...(episode.summary !== undefined ? { summary: episode.summary } : {}),
  }));
}

interface AgentStructureReviewProps {
  readonly projectId: string;
  readonly request: AgentGenerationRequest;
  /**
   * Called after the structure is approved. Receives the NEW GenerationRequest
   * that tracks the script-generation work (a fresh request with its own
   * generationRequestId and the new courseId), so the caller can follow it.
   */
  onApproved(request: AgentGenerationRequest): Promise<void> | void;
  /** Optional reject/regenerate action; omitted (e.g. from Drafts) hides it. */
  onRegenerate?(): Promise<void> | void;
  regenerating?: boolean;
  /** Optional discard/cancel action (e.g. collapse the inline flow). */
  onDiscard?(): void;
}

// The editable proposed-structure review table plus the approve action, shared
// by the inline agent-generate flow (AgentGenerateSection) and the Drafts
// detail screen. It owns the editable-episode state (seeded from the request's
// proposedStructure) and the approveCourseStructure mutation; the count/title/
// duration validation mirrors the server (Property 10, Req 5.3).
export function AgentStructureReview({
  projectId,
  request,
  onApproved,
  onRegenerate,
  regenerating = false,
  onDiscard,
}: AgentStructureReviewProps): ReactNode {
  const { t } = useT();
  const [episodes, setEpisodes] = useState<ReadonlyArray<EditableEpisode>>(() =>
    episodesFromRequest(request),
  );
  const [reviewError, setReviewError] = useState<string | undefined>(undefined);

  // Reseed the table when the underlying request changes (e.g. navigating
  // between drafts or a fresh proposal arriving).
  useEffect(() => {
    setEpisodes(episodesFromRequest(request));
    setReviewError(undefined);
  }, [request]);

  const [approveStructure, { loading: approving }] = useMutation<
    ApproveCourseStructureData,
    ApproveCourseStructureVariables
  >(APPROVE_COURSE_STRUCTURE);

  const episodeCount = episodes.length;
  const countValid = isValidEpisodeCount(episodeCount);
  // The agent's reasoning about the course structure (read-only), shown above
  // the editable table so the creator sees "what the agent is thinking".
  const overview = parseProposedOverview(request.proposedStructure);

  function updateEpisode(index: number, patch: Partial<EditableEpisode>): void {
    setEpisodes((current) =>
      current.map((episode, i) => (i === index ? { ...episode, ...patch } : episode)),
    );
  }

  function addEpisode(): void {
    setEpisodes((current) => {
      if (current.length >= EPISODE_COUNT_MAX) {
        return current;
      }
      return [...current, { title: '', durationText: '60' }];
    });
  }

  function removeEpisode(index: number): void {
    setEpisodes((current) => {
      if (current.length <= EPISODE_COUNT_MIN) {
        return current;
      }
      return current.filter((_, i) => i !== index);
    });
  }

  async function handleApprove(): Promise<void> {
    setReviewError(undefined);
    if (!isValidEpisodeCount(episodes.length)) {
      setReviewError(
        t('agent.episodeCountRangeError', { min: EPISODE_COUNT_MIN, max: EPISODE_COUNT_MAX }),
      );
      return;
    }

    const normalized = episodes.map((episode, index) => {
      const duration = Number.parseInt(episode.durationText, 10);
      return {
        episodeIndex: index,
        title: episode.title.trim(),
        proposedDurationSeconds: duration,
      };
    });

    if (normalized.some((episode) => episode.title.length === 0)) {
      setReviewError(t('agent.episodeTitleRequired'));
      return;
    }
    if (
      normalized.some(
        (episode) =>
          !Number.isInteger(episode.proposedDurationSeconds) ||
          episode.proposedDurationSeconds <= 0,
      )
    ) {
      setReviewError(t('agent.episodeDurationRequired'));
      return;
    }

    try {
      const result = await approveStructure({
        variables: {
          input: {
            projectId,
            generationRequestId: request.generationRequestId,
            episodes: normalized,
          },
        },
      });
      const created = result.data?.approveCourseStructure;
      if (created === undefined) {
        setReviewError(t('agent.approveError'));
        return;
      }
      await onApproved(created);
    } catch (err) {
      setReviewError(errorMessage(err, t('agent.approveError')));
    }
  }

  return (
    <div className="agent-review">
      <h5>{t('agent.proposedStructure')}</h5>
      <p className="field-hint">
        {t('agent.reviewHint', { min: EPISODE_COUNT_MIN, max: EPISODE_COUNT_MAX })}
      </p>

      {overview !== undefined && (
        <div className="agent-overview">
          <span className="agent-overview-label">{t('agent.overviewLabel')}</span>
          <p className="agent-overview-text">{overview}</p>
        </div>
      )}

      <ul className="agent-episode-list">
        {episodes.map((episode, index) => (
          <li key={index} className="agent-episode-row">
            <span className="agent-episode-index">{index + 1}</span>
            <input
              className="agent-episode-title"
              type="text"
              value={episode.title}
              onChange={(e) => updateEpisode(index, { title: e.target.value })}
              placeholder={t('agent.episodeTitlePlaceholder')}
              aria-label={t('agent.episodeTitleLabel', { index: index + 1 })}
            />
            <input
              className="agent-episode-duration"
              type="number"
              min={1}
              value={episode.durationText}
              onChange={(e) => updateEpisode(index, { durationText: e.target.value })}
              aria-label={t('agent.episodeDurationLabel', { index: index + 1 })}
            />
            <span className="agent-episode-unit">s</span>
            <button
              type="button"
              className="agent-episode-remove"
              onClick={() => removeEpisode(index)}
              disabled={episodes.length <= EPISODE_COUNT_MIN}
              aria-label={t('agent.removeEpisodeLabel', { index: index + 1 })}
            >
              {t('agent.removeEpisode')}
            </button>
            {episode.summary !== undefined && (
              <p className="agent-episode-summary">{episode.summary}</p>
            )}
          </li>
        ))}
      </ul>

      <div className="agent-review-tools">
        <button type="button" onClick={addEpisode} disabled={episodes.length >= EPISODE_COUNT_MAX}>
          {t('agent.addEpisode')}
        </button>
        <span className="agent-episode-count">
          {t('agent.episodeCount', { count: episodeCount, max: EPISODE_COUNT_MAX })}
        </span>
      </div>

      {reviewError && <p className="form-error">{reviewError}</p>}

      <div className="agent-review-actions">
        <button
          type="button"
          className="agent-approve"
          onClick={() => void handleApprove()}
          disabled={approving || !countValid}
        >
          {approving ? t('agent.approving') : t('agent.approve')}
        </button>
        {onRegenerate && (
          <button
            type="button"
            className="agent-reject"
            onClick={() => void onRegenerate()}
            disabled={regenerating}
          >
            {regenerating ? t('agent.requesting') : t('agent.reject')}
          </button>
        )}
        {onDiscard && (
          <button type="button" onClick={onDiscard} disabled={approving || regenerating}>
            {t('agent.discard')}
          </button>
        )}
      </div>
    </div>
  );
}
